import React, { useState, useEffect } from "react";
import { usersAPI } from "../services/api";
import SearchableSelect from "./SearchableSelect";

const ROLE_OPTIONS = [
  { value: "user",  label: "User" },
  { value: "admin", label: "Administrator" },
];

const UserModal = ({ user, onClose }) => {
  const [formData, setFormData] = useState({
    username:        "",
    password:        "",
    confirmPassword: "",
    full_name:       "",
    role:            "user",
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (user) {
      setFormData({
        username:        user.username  || "",
        password:        "",
        confirmPassword: "",
        full_name:       user.full_name || "",
        role:            user.role      || "user",
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!formData.username.trim() || !formData.full_name.trim()) {
      setError("Username and full name are required");
      setLoading(false);
      return;
    }
    if (!user && !formData.password) {
      setError("Password is required for new users");
      setLoading(false);
      return;
    }
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }
    if (formData.password && formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    try {
      if (user) {
        const updateData = { full_name: formData.full_name, role: formData.role };
        if (formData.password) updateData.password = formData.password;
        await usersAPI.update(user.id, updateData);
      } else {
        await usersAPI.create(formData);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h3 className="text-lg font-semibold text-gray-900">
              {user ? "Edit User" : "Add New User"}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-5 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="username" className="form-label">Username *</label>
              <input
                type="text"
                id="username"
                name="username"
                required
                className="form-input"
                placeholder="Enter username"
                value={formData.username}
                onChange={handleChange}
                disabled={loading || !!user}
              />
              {user && (
                <p className="mt-1 text-xs text-gray-500">Username cannot be changed for existing users</p>
              )}
            </div>

            <div>
              <label htmlFor="full_name" className="form-label">Full Name *</label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                required
                className="form-input"
                placeholder="Enter full name"
                value={formData.full_name}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div>
              <label className="form-label">Role *</label>
              <SearchableSelect
                value={formData.role}
                onChange={v => setFormData(prev => ({ ...prev, role: v }))}
                options={ROLE_OPTIONS}
                placeholder="Please select role"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="form-label">
                Password {!user && "*"}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                required={!user}
                className="form-input"
                placeholder={user ? "Leave empty to keep current password" : "Enter password"}
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
              />
              {!user && (
                <p className="mt-1 text-xs text-gray-500">Password must be at least 6 characters long</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password {!user && "*"}
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                required={!user || !!formData.password}
                className="form-input"
                placeholder="Confirm password"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            {formData.role === "admin" && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                <svg className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-yellow-700">
                  <strong>Warning:</strong> Admin users have full access to the system including user management.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-4 bg-gray-50 rounded-b-xl flex flex-col sm:flex-row-reverse gap-3">
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full sm:w-auto"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="loading-spinner" /> Saving...
                </span>
              ) : user ? "Update User" : "Create User"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn btn-secondary w-full sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;
